import {
    correlationIdHeader,
    genCorrelationId,
    UnauthenticatedError,
} from "@sp-crm/core";
import { GraphQLFormattedError } from "graphql";
import { extraRequestHeaders } from "util/browser";
import { setErrorTrackingContext } from "util/error-tracking";

export class ClientGraphqlError extends Error {
    constructor(public readonly graphqlErrors: GraphQLFormattedError[]) {
        super(ClientGraphqlError.getFirstErrorMessage(graphqlErrors));

        this.hasErrorType = (errorType: string) => {
            return this.graphqlErrors.some(
                e => e.extensions && e.extensions["errorType"] === errorType,
            );
        };
    }

    public hasErrorType = (errorType: string): boolean => {
        return this.graphqlErrors.some(
            e => e.extensions && e.extensions["errorType"] === errorType,
        );
    };

    public hasMessageContaining = (needle: string): boolean => {
        return this.graphqlErrors.some(e => e.message.includes(needle));
    };

    static getFirstErrorMessage(graphqlErrors: GraphQLFormattedError[]) {
        if (graphqlErrors && graphqlErrors.length > 0) {
            return graphqlErrors[0]?.message;
        }

        return "An unknown error occurred.";
    }
}

export class ClientGraphqlNetworkError extends Error {
    constructor(public readonly originalError: Error) {
        super(`Network error: ${originalError?.message || "Fetch error"}`);
        this.name = "ClientGraphqlNetworkError";
    }
}

export function graphqlFetcher<TData, TVariables>(query: string, variables?: TVariables) {
    return async (): Promise<TData> => {
        let res: Response | null = null;

        try {
            res = await fetch("/api/graphql" as string, {
                method: "POST",
                ...{
                    headers: {
                        ...extraRequestHeaders(),
                        "content-type": "application/json",
                        [correlationIdHeader]: genCorrelationId("client"),
                    },
                },
                body: JSON.stringify({ query, variables }),
            });
        } catch (e) {
            throw new ClientGraphqlNetworkError(
                e instanceof Error ? e : new Error(String(e)),
            );
        }

        if (res.status === 401) {
            throw new UnauthenticatedError();
        }

        const json = await res.json();

        const setGraphqlErrorContext = () => {
            setErrorTrackingContext({
                query,
            });
        };

        if (json.errors) {
            setGraphqlErrorContext();
            throw new ClientGraphqlError(json.errors);
        }

        if (!res.ok) {
            setGraphqlErrorContext();
            throw new ClientGraphqlError([
                {
                    message: "An unknown error occurred.",
                    locations: [],
                },
            ]);
        }

        return json.data;
    };
}
